import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useHeroBanners } from '@/hooks/useHeroBanners';
import { heroBannerToItem, isBannerVisible, buildDefaultHeroBanner } from '@/lib/heroBanners';
import HeroBannerSlider from '@/components/feature/HeroBannerSlider';
import { useHomeConfig } from '@/hooks/useHomeConfig';
import { normalizeBlockConfig, orderedEnabledBlocks } from '@/lib/homeBlocks';
import { htmlToText, resolveStoreName } from '@/lib/display';
import { normalizeCurrency } from '@/lib/currency';
import {
  SectionHeading,
  ServicesGrid,
  PortfolioGrid,
  TestimonialsGrid,
  TeamGrid,
  PartnersGrid,
  AboutSection,
  type ServiceRow,
  type ProjectRow,
  type PersonRow,
} from './HomeSections';
import { CtaSection, BookingCalendarSection } from './HomeExtraSections';
import ZifekBlogSection from '@/components/feature/ZifekBlogSection';
import ThemeExtraBlocksRenderer from '@/components/feature/ThemeExtraBlocksRenderer';
import HomeEventsSection from './HomeEventsSection';

// ─────────────────────────────────────────────────────────────
// Page d'accueil publique — type de compte « Services » (cas 2)
// Ordre des blocs entièrement piloté par la config du marchand
// (dashboard → Blocs de la page d'accueil).
// Blocs : Bannière → Services en vedette → Nos services (3×3) →
//         Portfolio → Témoignages → Équipe → Partenaires
// ─────────────────────────────────────────────────────────────

const GRID_LIMIT = 9;
const FEATURED_LIMIT = 3;

export default function ServicesHome() {
  const { tenant, theme, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const { banners } = useHeroBanners(merchantId);
  const { config, loading: configLoading } = useHomeConfig(merchantId);

  const [services, setServices] = useState<ServiceRow[]>([]);
  const [featured, setFeatured] = useState<ServiceRow[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [testimonials, setTestimonials] = useState<PersonRow[]>([]);
  const [team, setTeam] = useState<PersonRow[]>([]);
  const [partners, setPartners] = useState<PersonRow[]>([]);
  const [loading, setLoading] = useState(true);

  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const storeName = resolveStoreName(tenant, theme);
  const storeDescription = htmlToText(tenant?.description || tenant?.aboutus);
  const aboutTitle = config.aboutTitle.trim() || 'À propos';
  const aboutText = config.aboutContent.trim() || storeDescription;

  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    (async () => {
      try {
        const [svcRes, featRes, portRes, testiRes, teamRes, partRes] = await Promise.all([
          supabase
            .from('nospartenairesservices')
            .select('id, titre, description, product_image, prix, prix_promo, tags')
            .eq('idcommerce', merchantId)
            .order('created_at', { ascending: false })
            .limit(GRID_LIMIT),
          supabase
            .from('nospartenairesservices')
            .select('id, titre, description, product_image, prix, prix_promo, tags')
            .eq('idcommerce', merchantId)
            .eq('is_featured', true)
            .order('created_at', { ascending: false })
            .limit(FEATURED_LIMIT),
          supabase
            .from('portfolio')
            .select('id, titre, description, product_image, prix, tags, ville, pays')
            .eq('idcommerce', merchantId)
            .order('created_at', { ascending: false })
            .limit(9),
          supabase
            .from('temoignage')
            .select('id, titre, description, detailssup, product_image, tags')
            .eq('idcommerce', merchantId)
            .order('created_at', { ascending: false })
            .limit(6),
          supabase
            .from('equipe')
            .select('id, titre, description, detailssup, product_image, tags')
            .eq('idcommerce', merchantId)
            .order('created_at', { ascending: false })
            .limit(8),
          supabase
            .from('partenaires')
            .select('id, titre, description, detailssup, product_image, tags')
            .eq('idcommerce', merchantId)
            .order('created_at', { ascending: false })
            .limit(6),
        ]);
        if (cancelled) return;
        setServices((svcRes.data as ServiceRow[]) || []);
        setFeatured((featRes.data as ServiceRow[]) || []);
        setProjects((portRes.data as ProjectRow[]) || []);
        setTestimonials((testiRes.data as PersonRow[]) || []);
        setTeam((teamRes.data as PersonRow[]) || []);
        setPartners((partRes.data as PersonRow[]) || []);
      } catch {
        // silencieux
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId]);

  const heroItems = banners.filter(isBannerVisible).map(heroBannerToItem);
  const defaultHero = heroItems.length === 0
    ? buildDefaultHeroBanner({ image: tenant?.imageaboutus, title: storeName, description: storeDescription })
    : null;
  const heroDisplay = defaultHero ? [defaultHero] : heroItems;
  const blocks = normalizeBlockConfig(config.blocks);
  const order = orderedEnabledBlocks(blocks, 'services');

  if (loading || configLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  const renderBlock = (key: string) => {
    switch (key) {
      case 'banner':
        return heroDisplay.length > 0 ? (
          <HeroBannerSlider key="banner" banners={heroDisplay} />
        ) : (
          <section key="banner" className="relative py-14 md:py-20">
            <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
            <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-3">{storeName}</h1>
              {storeDescription && (
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                  {storeDescription}
                </p>
              )}
            </div>
          </section>
        );

      case 'featured_services':
        return featured.length > 0 ? (
          <section key="featured_services" className="py-10 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <SectionHeading icon="ri-star-fill" title="Services en vedette" />
              <ServicesGrid services={featured} monaie={monaie} badge="Vedette" />
            </div>
          </section>
        ) : null;

      case 'services':
        return services.length > 0 ? (
          <section key="services" className="py-10 md:py-16 bg-background-100/50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <SectionHeading icon="ri-service-line" title="Nos services" linkTo="/services" />
              <ServicesGrid services={services} monaie={monaie} columns={config.servicesColumns} />
              <div className="mt-8 flex justify-center">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  Voir plus de services
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>
        ) : null;

      case 'portfolio':
        return projects.length > 0 ? (
          <section key="portfolio" className="py-10 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <SectionHeading icon="ri-briefcase-line" title="Nos réalisations" linkTo="/portfolio" />
              <PortfolioGrid items={projects} monaie={monaie} />
            </div>
          </section>
        ) : null;

      case 'testimonials':
        return testimonials.length > 0 ? (
          <section key="testimonials" className="py-10 md:py-16 bg-background-100/50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <SectionHeading icon="ri-chat-quote-line" title="Nos témoignages" />
              <TestimonialsGrid items={testimonials} />
            </div>
          </section>
        ) : null;

      case 'team':
        return team.length > 0 ? (
          <section key="team" className="py-10 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <SectionHeading icon="ri-user-star-line" title="Notre équipe" linkTo="/team" />
              <TeamGrid members={team} />
            </div>
          </section>
        ) : null;

      case 'partners':
        return partners.length > 0 ? (
          <section key="partners" className="py-10 md:py-16 bg-background-100/50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <SectionHeading icon="ri-team-line" title="Nos partenaires" linkTo="/partners" />
              <PartnersGrid partners={partners} />
            </div>
          </section>
        ) : null;

      case 'cta':
        return config.ctaTitle.trim() || config.ctaText.trim() ? (
          <CtaSection
            key="cta"
            title={config.ctaTitle}
            text={config.ctaText}
            buttonLabel={config.ctaButtonLabel}
            url={config.ctaUrl}
          />
        ) : null;

      case 'booking_calendar':
        return <BookingCalendarSection key="booking_calendar" serviceId={config.defaultBookingServiceId} />;

      case 'about':
        return aboutText ? (
          <AboutSection key="about" title={aboutTitle} content={aboutText} />
        ) : null;

      default:
        return null;
    }
  };

  const rendered = order.map(renderBlock).filter(Boolean);

  // Aucun contenu disponible
  if (rendered.length === 0) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center py-24 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-service-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">{storeName}</h2>
        <p className="text-sm text-foreground-500">Aucun contenu pour le moment</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {rendered}
      <HomeEventsSection />
      <ThemeExtraBlocksRenderer />
      <ZifekBlogSection />
    </div>
  );
}